import React from 'react';
import './ConfirmDialog.css';

// Built-in icon variants
const ICONS = {
  delete: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
  ),
  lock: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  warning: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
};

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  icon?: keyof typeof ICONS;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  danger = true,
  icon = 'delete',
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  return (
    <div className="cd-overlay" onClick={onCancel}>
      <div className="cd-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`cd-icon-wrap${icon === 'lock' ? ' cd-icon-wrap-lock' : ''}`}>
          {ICONS[icon]}
        </div>
        <h3 className="cd-title">{title}</h3>
        <p className="cd-message">{message}</p>
        <div className="cd-actions">
          <button className="cd-btn-cancel" onClick={onCancel}>{cancelLabel}</button>
          <button
            className={`cd-btn-confirm${danger ? ' cd-btn-danger' : ' cd-btn-primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook for easy usage
export const useConfirmDialog = () => {
  const [state, setState] = React.useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    icon?: keyof typeof ICONS;
    danger?: boolean;
    resolve?: (confirmed: boolean) => void;
  }>({ open: false, title: '', message: '' });

  const confirm = (
    title: string,
    message: string,
    confirmLabel = 'Delete',
    icon: keyof typeof ICONS = 'delete',
    danger = true
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ open: true, title, message, confirmLabel, icon, danger, resolve });
    });
  };

  const handleConfirm = () => {
    state.resolve?.(true);
    setState(s => ({ ...s, open: false }));
  };

  const handleCancel = () => {
    state.resolve?.(false);
    setState(s => ({ ...s, open: false }));
  };

  const dialog = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      icon={state.icon}
      danger={state.danger}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, dialog };
};
